// Pantalla intermedia de los atajos de "Ediciones rápidas" —
// ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
//
// Cuatro de los siete atajos (Editar, Recortar, Mejorar, Descripción) navegaban al
// historial y ahí se terminaba: el usuario tocaba "Mejorar" y aterrizaba en una grilla
// de creaciones sin ninguna señal de qué había pedido.
//
// El layout es EL MISMO que el del modo Carrusel, a pedido explícito
// (ADR-2026-08-20-la-pantalla-de-atajos-copia-el-layout-del-carrusel): panel `fixed` que
// tapa el contenido, hero a la izquierda + card blanca de opciones a la derecha en
// desktop, y en mobile carátula full-bleed con panel blanco redondeado abajo y la grilla
// dentro de un bottom sheet. No es un parecido: se replican las mismas clases, los mismos
// breakpoints y el mismo `left: sidebarWidth`.
//
// SÓLO "elegir una ya generada" en los atajos de siempre: los efectos que corren en el
// backend piden el `id` de una fila de `imagenes_generadas` —`/v1/images/upscale` y
// `/v1/images/instagram-description` con `image_id: uuid`, `/v1/images/edit` con
// `imagen_id: uuid`— y una foto subida no tiene fila. Por eso esos cuatro no tienen el
// botón azul "Subir imagen" que el carrusel sí tiene: sería prometer algo que corta al
// final del flujo.
//
// La EXCEPCIÓN es "Expansión mágica" (`conSubirPropia`), cuyo backend aprendió a
// aceptar una referencia de storage además del id de fila — el mismo contrato
// bucket+path del carrusel. Ese atajo, elegida la fuente (generada o subida), pasa a un
// SEGUNDO PASO a pantalla completa en este mismo layout (como el "configurar" del
// carrusel — pedido explícito: acá no va el sheet flotante del visor): el hero muestra
// el preview del encuadre nuevo y la card la elección de formato. Al expandir navega a
// la galería de recién generadas (`GeneratedImagesInline`), igual que el carrusel.
// Ver ADR-2026-08-25-expansion-magica-atajo-home y
// ADR-2026-08-25-expansion-atajo-configura-en-pantalla.

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Crop,
  Expand,
  ImageUp,
  Images,
  Info,
  Instagram,
  Loader2,
  Maximize,
  Pencil,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import OptionSheet from '../../../components/opciones/OptionSheet';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  CREDITOS_EXPANSION,
  encuadreOriginal,
  expandirImagen,
  ladoQueCrece,
  opcionesParaImagen,
  type RatioExpansion,
} from '../../../lib/expansionMagica';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';
import {
  GeneratedImagesHistory,
  type AccionVisor,
} from '../../GeneratedImagesHistory';

/** Los atajos que pasan por acá. El slug es el `:accion` de la URL. */
export type SlugAtajo =
  | 'editar'
  | 'recortar'
  | 'mejorar'
  | 'descripcion'
  | 'expansion';

interface ConfigAtajo {
  /** Título del bloque de la card, en imperativo: es la consigna del paso. */
  titulo: string;
  /** Qué va a pasar con la imagen elegida. */
  bajada: string;
  /** Copy del hero (desktop), abajo de la carátula. */
  heroDesc: string;
  Icono: LucideIcon;
  /**
   * Carátula del atajo. `null` = todavía no hay arte propio y se dibuja el ícono grande
   * en su lugar. Sólo Editar tiene una real (`editar_prompt.png`, el antes/después con la
   * flecha); las otras tres están PENDIENTES de arte, igual que en su momento lo
   * estuvieron las de Carrusel y Shorts Studio.
   */
  hero: string | null;
  /** Texto del vacío de la grilla, nombrando lo que este atajo hace. */
  emptyHint: string;
  /**
   * Muestra el botón azul "Subir imagen" (el del carrusel). Sólo lo declara la
   * expansión: es el único efecto cuyo backend acepta una foto sin fila.
   */
  conSubirPropia?: boolean;
  /**
   * Cómo se aplica el efecto:
   *  - `visor`: se monta el visor del historial (`soloVisor`) con la acción disparada.
   *    Son los que NO tienen pantalla propia y viven adentro de ese componente.
   *  - `ruta`: el efecto ya tiene su pantalla; se navega con el id de la imagen.
   *  - `expansion`: segundo paso EN ESTA PANTALLA (preview del encuadre en el hero +
   *    formatos en la card, como el "configurar" del carrusel); al expandir se navega
   *    a la galería de recién generadas.
   */
  destino:
    | { tipo: 'visor'; accion: AccionVisor }
    | { tipo: 'ruta'; armar: (imageId: string, companyId: string) => string }
    | { tipo: 'expansion' };
}

const ATAJOS: Record<SlugAtajo, ConfigAtajo> = {
  editar: {
    titulo: 'Elegí la imagen a editar',
    bajada: 'Después le pedís el cambio con un prompt y Atlas lo aplica.',
    heroDesc:
      'Pedile el cambio en palabras —otro fondo, otro color, sacar algo— y Atlas lo resuelve sobre tu foto.',
    Icono: Pencil,
    hero: '/images/editar_prompt.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para editarla.',
    // Editar SÍ tiene pantalla propia y sabe recibir la imagen por query params
    // (`EditarImagen` lee `?id=` y `?companyId=`), así que no hace falta el visor.
    destino: {
      tipo: 'ruta',
      armar: (imageId, companyId) =>
        `/redes-sociales/editar-imagen?id=${encodeURIComponent(imageId)}&companyId=${encodeURIComponent(companyId)}`,
    },
  },
  recortar: {
    titulo: 'Elegí la imagen a recortar',
    bajada: 'Después ajustás el encuadre y la descargás.',
    heroDesc:
      'Ajustá el encuadre a la proporción que necesites para cada red, sin salir de Atlas.',
    Icono: Crop,
    hero: null,
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para recortarla.',
    destino: { tipo: 'visor', accion: 'recortar' },
  },
  mejorar: {
    titulo: 'Elegí la imagen a mejorar',
    bajada: 'Atlas le sube la resolución con IA, sin perder detalle.',
    heroDesc:
      'Subí la resolución de una creación con IA: sirve para imprimir o para publicarla en grande.',
    Icono: Maximize,
    hero: null,
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para mejorarla.',
    destino: { tipo: 'visor', accion: 'mejorar' },
  },
  descripcion: {
    titulo: 'Elegí la imagen a publicar',
    bajada: 'Atlas te escribe el texto del post a partir de la foto.',
    heroDesc:
      'Atlas mira la imagen y escribe el texto para publicarla, con hashtags incluidos.',
    Icono: Instagram,
    hero: null,
    emptyHint:
      'Todavía no generaste ninguna imagen. Generá una y volvé para escribir su descripción.',
    destino: { tipo: 'visor', accion: 'descripcion' },
  },
  expansion: {
    titulo: 'Elegí la imagen a expandir',
    bajada: 'Después elegís el formato y la IA pinta lo que falta alrededor.',
    heroDesc:
      'Llevá una foto a otro encuadre sin recortarla: la imagen queda intacta y la IA pinta el lienzo nuevo.',
    Icono: Expand,
    hero: null,
    emptyHint: 'Todavía no generaste ninguna imagen. Subí una foto para expandirla.',
    // Único atajo con foto propia: su backend acepta bucket+path (ver arriba).
    conSubirPropia: true,
    destino: { tipo: 'expansion' },
  },
};

/** ¿Es un slug de atajo conocido? */
export function esSlugAtajo(v: string | undefined): v is SlugAtajo {
  return !!v && v in ATAJOS;
}

export default function AtajoImagenMode() {
  const navigate = useNavigate();
  const { accion } = useParams<{ accion: string }>();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();

  const config = esSlugAtajo(accion) ? ATAJOS[accion] : null;

  const [companyId, setCompanyId] = useState<string | null>(null);
  /** La imagen elegida. `null` = todavía estamos en el paso de elegir. */
  const [elegida, setElegida] = useState<GeneradaPick | null>(null);
  /** Mobile: la grilla vive en un sheet, así el panel de abajo no se llena. */
  const [sheetGeneradas, setSheetGeneradas] = useState(false);

  // ── Sólo el atajo de expansión ──
  /** La fuente a expandir: fila generada O foto subida. Con esto puesto, la pantalla
   *  está en el paso de elegir formato. `url` es sólo para el preview. */
  const [fuenteExpansion, setFuenteExpansion] = useState<{
    imagenId?: string;
    subida?: { bucket: string; path: string };
    url: string;
  } | null>(null);
  const [expandiendo, setExpandiendo] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const [errorExpansion, setErrorExpansion] = useState<string | null>(null);
  /** Medidas REALES de la fuente: definen qué formatos agregan lienzo y cuánto ocupa
   *  la original dentro del marco destino (misma mecánica que el sheet del visor). */
  const [medidas, setMedidas] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [errorMedidas, setErrorMedidas] = useState(false);
  const [ratio, setRatio] = useState<RatioExpansion | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando (patrón del carrusel).
  const uploadSeqRef = useRef(0);

  /** Vuelve del paso de formato al de elegir imagen (lo mismo que hace el carrusel). */
  const volverAElegir = () => {
    setFuenteExpansion(null);
    setErrorExpansion(null);
  };

  const opciones = opcionesParaImagen(medidas.w, medidas.h);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (!cancelado) {
        setCompanyId((p as { id_company?: string | null } | null)?.id_company ?? null);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Un slug que no existe no muestra una pantalla rota: vuelve a la home.
  useEffect(() => {
    if (!config) navigate('/interfaz-v2', { replace: true });
  }, [config, navigate]);

  // Back flotante del shell en mobile, igual que Carrusel y Animar: además de dar el
  // botón, esconde la barra inferior (se apaga con `mobileBackOverride !== null`).
  // En el paso de formato de la expansión vuelve a elegir imagen, no a la home
  // (misma conducta que el `paso === 'configurar'` del carrusel).
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (expandiendo) return; // no se abandona una expansión en vuelo
      if (fuenteExpansion) volverAElegir();
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuenteExpansion, expandiendo]);

  // Las medidas se sacan de un preloader y NO del `onLoad` del <img> del preview: la
  // imagen elegida puede estar ya en caché y su `load` dispara antes de que React
  // enganche el handler — `complete` cubre ese caso. Sin `crossOrigin`, para no caer
  // en otra partición de caché y re-descargar (ADR-083). Misma jugada que el sheet
  // del visor.
  useEffect(() => {
    const url = fuenteExpansion?.url;
    if (!url) {
      setMedidas({ w: 0, h: 0 });
      setErrorMedidas(false);
      setRatio(null);
      return;
    }
    let cancelado = false;
    const medir = (img: HTMLImageElement) => {
      if (!cancelado && img.naturalWidth > 0) {
        setMedidas({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    const img = new Image();
    img.onload = () => medir(img);
    // Sin medidas no hay herramienta: se dice, en vez de dejar "Cargando…" para
    // siempre (una URL firmada vencida entra justo por acá).
    img.onerror = () => {
      if (!cancelado) setErrorMedidas(true);
    };
    img.src = url;
    if (img.complete) medir(img);
    return () => {
      cancelado = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [fuenteExpansion?.url]);

  // Preselección: el primer formato que realmente expande. Recién cuando la imagen
  // cargó (antes no sabemos el encuadre actual).
  useEffect(() => {
    if (ratio !== null) return;
    const primera = opciones.find((o) => o.disponible);
    if (primera) setRatio(primera.id);
  }, [opciones, ratio]);

  if (!config) return null;

  /**
   * Saldo al ELEGIR la imagen, no al confirmar (mismo criterio que el visor: elegir
   * un formato para enterarse después de que no alcanzan los créditos es peor que no
   * abrir el panel). Falla ABIERTA: si la query no responde, el backend corta con 402.
   */
  const saldoAlcanza = async (): Promise<boolean> => {
    if (!companyId) return true;
    try {
      const { data } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', companyId)
        .single();
      const restantes = (data as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      if (typeof restantes === 'number' && restantes < CREDITOS_EXPANSION) {
        setLimitModal(
          (data as { plan?: string } | null)?.plan === 'gratis'
            ? 'free-trial-ended'
            : 'plan-limit-reached',
        );
        return false;
      }
    } catch {
      /* falla abierta */
    }
    return true;
  };

  const alElegir = (pick: GeneradaPick) => {
    setSheetGeneradas(false);
    if (config.destino.tipo === 'expansion') {
      void (async () => {
        if (await saldoAlcanza()) {
          setFuenteExpansion({ imagenId: pick.id, url: pick.signedUrl });
        }
      })();
      return;
    }
    if (config.destino.tipo === 'ruta') {
      if (!companyId) return;
      navigate(config.destino.armar(pick.id, companyId));
      return;
    }
    setElegida(pick);
  };

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setErrorSubida('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setErrorSubida(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId, 'expansion');
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      if (await saldoAlcanza()) {
        setFuenteExpansion({
          subida: { bucket: subida.bucket, path: subida.path },
          url: subida.previewUrl,
        });
      }
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setErrorSubida(
        err instanceof Error ? err.message : 'No se pudo subir la imagen.',
      );
    } finally {
      if (seq === uploadSeqRef.current) setSubiendo(false);
    }
  };

  const expandir = async () => {
    if (!fuenteExpansion || !companyId || expandiendo || !ratio) return;
    setExpandiendo(true);
    setErrorExpansion(null);
    try {
      const nueva = await expandirImagen({
        imagenId: fuenteExpansion.imagenId,
        subida: fuenteExpansion.subida,
        aspectRatio: ratio,
        companyId,
        modoUtilizado: 'expansion_magica_atajo',
      });
      // La galería de recién generadas (GeneratedImagesInline), igual que el carrusel.
      // La tanda es la imagen nueva sola, ya firmada por `expandirImagen` — va
      // precargada y el inline no tiene que pollear nada.
      navigate('/redes-sociales/imagenes-generadas', {
        state: {
          imageIds: [nueva.id],
          companyId,
          preloadedImages: [
            {
              id: nueva.id,
              fileName: nueva.fileName,
              signedUrl: nueva.signedUrl,
              favorito: false,
            },
          ],
        },
      });
    } catch (error) {
      if (error instanceof GenerateAdError && error.errorType === 'billing') {
        setLimitModal('plan-limit-reached');
      } else {
        setErrorExpansion(
          error instanceof Error
            ? error.message
            : 'No pudimos procesar la imagen. Intentá nuevamente en unos minutos.',
        );
      }
    } finally {
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* sin listeners no pasa nada */
      }
      setExpandiendo(false);
    }
  };

  // Paso 2 (sólo los `visor`): el visor del historial, prestado, con la acción ya
  // disparada. Al cerrarlo se vuelve al paso de elegir y no a la home: quien entró por
  // "Mejorar" probablemente quiera mejorar otra.
  if (elegida && config.destino.tipo === 'visor') {
    return (
      <GeneratedImagesHistory
        soloVisor={{
          imageId: elegida.id,
          accionInicial: config.destino.accion,
          onCerrar: () => setElegida(null),
        }}
      />
    );
  }

  const { Icono } = config;

  // ── Paso "elegir formato" de la expansión (ocupa ESTA pantalla, no un sheet) ──
  const enConfigurarExpansion =
    config.destino.tipo === 'expansion' && fuenteExpansion !== null;
  const marco = ratio
    ? opciones.find((o) => o.id === ratio)!
    : { id: '4:5' as RatioExpansion, etiqueta: '', w: 4, h: 5, disponible: false };
  const { anchoPct, altoPct } = ratio
    ? encuadreOriginal(ratio, medidas.w, medidas.h)
    : { anchoPct: 1, altoPct: 1 };
  const crece = ratio ? ladoQueCrece(ratio, medidas.w, medidas.h) : null;
  const listoParaExpandir = ratio !== null && medidas.w > 0 && !expandiendo;

  /**
   * El preview de la expansión, como hero del paso: el marco DESTINO con la original
   * nítida del tamaño exacto que va a ocupar, sobre la misma imagen borroneada como
   * volumen de lo que la IA completa. Es el dibujo del sheet del visor, en grande.
   * `maxAlto` manda: el ancho sale del ratio del marco (con tope para los apaisados).
   */
  const previewExpansion = (maxAlto: string) =>
    fuenteExpansion ? (
      <div
        className="relative overflow-hidden rounded-2xl bg-neutral-100 shadow-sm"
        style={{
          // `aspect-[w/h]` de Tailwind no compila en este proyecto: va inline.
          aspectRatio: `${marco.w} / ${marco.h}`,
          width: `min(92%, 480px, calc(${maxAlto} * ${marco.w} / ${marco.h}))`,
          transition: 'aspect-ratio 300ms ease-out, width 300ms ease-out',
        }}
      >
        <img
          src={fuenteExpansion.url}
          alt=""
          aria-hidden
          className={`absolute inset-0 w-full h-full object-cover blur-xl ${expandiendo ? 'expansion-latido' : 'opacity-80'}`}
          style={{ transform: 'scale(1.25)' }}
        />
        <img
          src={fuenteExpansion.url}
          alt="Vista previa de la expansión"
          className="absolute left-1/2 top-1/2 object-contain"
          style={{
            width: `${anchoPct * 100}%`,
            height: `${altoPct * 100}%`,
            transform: 'translate(-50%, -50%)',
            transition: 'width 300ms ease-out, height 300ms ease-out',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.65)',
          }}
        />
        {/* Brillo que barre el marco mientras corre la generación. */}
        {expandiendo && (
          <div
            aria-hidden
            className="absolute inset-y-0 -left-1/3 w-1/3 expansion-barrido pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)',
            }}
          />
        )}
      </div>
    ) : null;

  /** Contenido de la card en el paso de formato: selector + info + progreso. */
  const bloqueConfigurarExpansion = (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <Expand className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            Elegí el formato
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            Tu foto queda intacta: la IA pinta el lienzo que falta alrededor.
          </p>
        </div>
      </div>

      {/* El mismo selector del sheet del visor: 4 miniaturas de encuadre. */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
          Formato
        </p>
        <div className="grid grid-cols-4 gap-2">
          {opciones.map((op) => {
            const activo = op.id === ratio;
            return (
              <button
                key={op.id}
                type="button"
                disabled={!op.disponible || expandiendo}
                onClick={() => setRatio(op.id)}
                title={
                  op.disponible
                    ? op.etiqueta
                    : medidas.w === 0
                      ? 'Todavía estamos leyendo la imagen'
                      : 'La imagen ya tiene este formato'
                }
                className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border transition-colors ${
                  activo
                    ? 'border-[#1644EB] bg-[#EEF2FE]'
                    : 'border-neutral-200 bg-white hover:bg-neutral-50'
                } ${!op.disponible || expandiendo ? 'opacity-40 cursor-not-allowed hover:bg-white' : ''}`}
              >
                {/* Miniatura del encuadre: 22px de lado mayor. */}
                <span
                  className={`block rounded-[3px] border-2 ${activo ? 'border-[#1644EB]' : 'border-neutral-400'}`}
                  style={{
                    width: op.w >= op.h ? 22 : (22 * op.w) / op.h,
                    height: op.h >= op.w ? 22 : (22 * op.h) / op.w,
                  }}
                />
                <span
                  className={`text-[11px] font-medium ${activo ? 'text-[#1644EB]' : 'text-brand-black/70'}`}
                >
                  {op.id}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-[13px] text-[#3C3C43]/60 mt-2">
          {errorMedidas
            ? 'No pudimos leer la imagen. Volvé atrás y elegí otra.'
            : medidas.w === 0
              ? 'Cargando la imagen…'
              : crece
                ? `La IA completa ${crece === 'costados' ? 'los costados' : 'arriba y abajo'} sin recortar nada de la foto.`
                : 'Elegí un formato distinto al actual.'}
        </p>
      </div>

      <div className="mt-4">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
          Información
        </p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-2">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                expand_content
              </span>
              <span className="text-[15px] text-brand-black/90">
                Se guarda como imagen nueva
              </span>
            </div>
            <p className="text-[13px] text-[#3C3C43]/60">
              La original queda intacta en tu historial: la expandida se suma como una
              imagen aparte.
            </p>
          </div>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
              <span className="text-[13px] text-[#3C3C43]/60">
                {CREDITOS_EXPANSION === 1
                  ? 'Se consumirá 1 imagen'
                  : `Se consumirán ${CREDITOS_EXPANSION} imágenes`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Estado "expandiendo": misma barra que el sheet del visor (gris → primario,
          ~40s, sin porcentaje falso — la expansión suele resolverse antes). */}
      {expandiendo && (
        <div className="mt-4" role="status" aria-live="polite">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
            <span className="text-[13px] text-brand-black/80">
              Completando la escena…
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-neutral-200">
            <div
              className="h-full rounded-full"
              style={{
                width: '100%',
                transformOrigin: 'left',
                animation:
                  'expansion-progreso 40s cubic-bezier(0, 0, 0.2, 1) forwards',
                background:
                  'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)',
              }}
            />
          </div>
        </div>
      )}
      {errorExpansion && (
        <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
          {errorExpansion}
        </div>
      )}
    </>
  );

  /** CTA del paso de formato, pegado abajo de la card (fuera del scroll). */
  const botonExpandir = (variant: 'mobile' | 'desktop') => (
    <div
      className={`shrink-0 border-t border-black/[0.06] bg-white ${variant === 'mobile' ? 'px-5 pt-3 pb-5' : 'px-6 py-4'}`}
    >
      <button
        type="button"
        onClick={() => void expandir()}
        disabled={!listoParaExpandir}
        className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        {expandiendo ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> Expandiendo…
          </>
        ) : (
          <>
            <Expand className="w-5 h-5" /> Expandir
          </>
        )}
      </button>
    </div>
  );

  /** La carátula del atajo, o el ícono grande cuando todavía no hay arte propio. */
  const heroImg = (className: string) =>
    config.hero ? (
      <img src={config.hero} alt="" className={className} />
    ) : (
      // Mismo lenguaje que el chip de la card, en grande: cuadrado redondeado con el
      // tinte de marca. No es un placeholder gris — se lee como una pieza más del
      // sistema mientras no exista la carátula real.
      <div
        className={`${className} grid aspect-square w-full place-items-center rounded-2xl bg-[#1644EB]/[0.07]`}
      >
        <Icono className="h-16 w-16 text-brand-primary" strokeWidth={1.4} />
      </div>
    );

  /** Encabezado + CTA del aporte de imagen. Mismo bloque que el del carrusel, sin el
   *  botón azul de "Subir imagen" (ver el comentario de arriba del archivo). */
  const bloqueElegirImagen = (variant: 'mobile' | 'desktop') => (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <Icono className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            {config.titulo}
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            {config.bajada}
          </p>
        </div>
      </div>

      {/* El botón azul del carrusel, sólo en el atajo que sabe recibir una foto
          propia (hoy: expansión). */}
      {config.conSubirPropia && (
        <button
          type="button"
          onClick={elegirArchivo}
          disabled={subiendo}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-60 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          {subiendo ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <ImageUp className="w-5 h-5" /> Subir imagen
            </>
          )}
        </button>
      )}

      {variant === 'mobile' ? (
        <button
          type="button"
          onClick={() => setSheetGeneradas(true)}
          className={
            // Con "Subir imagen" arriba, éste pasa a secundario (blanco con borde,
            // como en el carrusel); solo, sigue siendo el CTA primario.
            config.conSubirPropia
              ? 'w-full mt-2.5 inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary text-brand-black text-[15.5px] font-semibold transition-colors'
              : 'w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors'
          }
        >
          <Images className="w-5 h-5" /> Elegir de mis generadas
        </button>
      ) : (
        <div
          className={`flex items-center gap-3 mb-1 ${config.conSubirPropia ? 'mt-5' : 'mt-1'}`}
        >
          <div className="flex-1 h-px bg-black/[0.06]" />
          <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
            {config.conSubirPropia
              ? 'o elegí una que ya generaste'
              : 'elegí una que ya generaste'}
          </span>
          <div className="flex-1 h-px bg-black/[0.06]" />
        </div>
      )}
      {errorSubida && (
        <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
          {errorSubida}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* ── MOBILE: carátula full-bleed + panel blanco redondeado abajo ────── */}
      <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
        <div className="relative flex-1 min-h-0 bg-neutral-100">
          {enConfigurarExpansion ? (
            // El preview del encuadre destino ocupa el lugar de la carátula. El tope
            // de alto deja aire para el -mt-6 del panel de abajo.
            <div className="absolute inset-0 flex items-center justify-center p-4 pb-10">
              {previewExpansion('34dvh')}
            </div>
          ) : config.hero ? (
            <img
              src={config.hero}
              alt=""
              className="absolute inset-0 w-full h-full object-contain p-6"
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <Icono className="h-20 w-20 text-brand-primary/70" strokeWidth={1.3} />
            </div>
          )}
        </div>
        <div
          className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
          style={{ maxHeight: '55dvh' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
            {enConfigurarExpansion
              ? bloqueConfigurarExpansion
              : bloqueElegirImagen('mobile')}
          </div>
          {enConfigurarExpansion && botonExpandir('mobile')}
        </div>
      </div>

      {/* ── DESKTOP: panel a la derecha de la sidebar, dos columnas ────────── */}
      <div
        className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-content rounded-tl-[26px] rounded-bl-[26px] flex-col"
        style={{ left: sidebarWidth }}
      >
        <header className="shrink-0 px-6 pt-5 pb-3">
          <button
            type="button"
            onClick={() => {
              // En el paso de formato, "Volver" es al paso de elegir imagen, no a la
              // home — y una expansión en vuelo no se abandona.
              if (enConfigurarExpansion) {
                if (!expandiendo) volverAElegir();
                return;
              }
              navigate('/interfaz-v2');
            }}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        </header>

        <div className="px-8 pb-6 flex flex-row gap-6 justify-center flex-1 min-h-0 items-stretch">
          {/* Columna izquierda: carátula del atajo + copy (o el preview del encuadre
              en el paso de formato de la expansión). */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 min-w-0">
            {enConfigurarExpansion ? (
              <>
                {previewExpansion('58vh')}
                <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
                  Tu foto, del tamaño exacto que va a ocupar en el encuadre nuevo.
                </p>
              </>
            ) : (
              <>
                {heroImg(
                  'max-w-[420px] max-h-[60%] w-auto h-auto object-contain rounded-2xl',
                )}
                <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
                  {config.heroDesc}
                </p>
              </>
            )}
          </div>

          {/* Columna derecha: card blanca con la grilla inline (o la elección de
              formato en el paso 2 de la expansión). */}
          <aside className="shrink-0 flex-1 max-w-[560px] flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-6">
              {enConfigurarExpansion ? (
                bloqueConfigurarExpansion
              ) : (
                <>
                  {bloqueElegirImagen('desktop')}
                  <div className="mt-3">
                    <GeneradasPickGrid
                      companyId={companyId}
                      selectedId={null}
                      onSelect={alElegir}
                      emptyHint={config.emptyHint}
                    />
                  </div>
                </>
              )}
            </div>
            {enConfigurarExpansion && botonExpandir('desktop')}
          </aside>
        </div>
      </div>

      {/* Mobile: la grilla en un bottom sheet, igual que el carrusel. */}
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        <GeneradasPickGrid
          companyId={companyId}
          selectedId={null}
          onSelect={alElegir}
          emptyHint={config.emptyHint}
        />
      </OptionSheet>

      {/* Sólo expansión: el input de la foto propia y el modal de límite del
          chequeo de saldo. */}
      {config.destino.tipo === 'expansion' && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFile}
          />
          <SubscriptionLimitModal
            isOpen={limitModal !== null}
            type={limitModal ?? 'plan-limit-reached'}
            onClose={() => setLimitModal(null)}
          />
        </>
      )}
    </>
  );
}
